import type { GetAssignableDesignersForRequestResponse } from "@nlc/shared-types";

import { useQuery } from "@tanstack/react-query";

import { assignableDesignersKeys } from "@/hooks/queryKeys";
import apiClient from "@/lib/api/client";

interface AssigneeOption {
  designation?: string;
  id: number;
  name: string;
}

interface UseAssigneeOptionsResult {
  assignees: AssigneeOption[];
}

/**
 * Hook for fetching assignee options for a specific request
 * Uses the assignable-designers endpoint which filters to team users only
 * Search filtering is done client-side
 */
export function useAssigneeOptions(
  requestId: number,
  search?: string,
  isEditing: boolean = true,
) {
  return useQuery({
    enabled: isEditing && !!requestId && requestId > 0,
    queryFn: async (): Promise<UseAssigneeOptionsResult> => {
      const response: GetAssignableDesignersForRequestResponse = await apiClient
        .url(`/requests/${requestId}/assignable-designers`)
        .get()
        .json();

      return { assignees: response.designers };
    },
    queryKey: assignableDesignersKeys.assignableDesignersByRequestId(requestId),
    select: (data): UseAssigneeOptionsResult => {
      if (!search || search.length < 2) {
        return data;
      }

      const searchLower = search.toLowerCase();

      return {
        assignees: data.assignees.filter(
          (a) =>
            a.name.toLowerCase().includes(searchLower) ||
            a.designation?.toLowerCase().includes(searchLower),
        ),
      };
    },
    staleTime: 10 * 60 * 1000,
  });
}
